import type { GameStateManager } from '../game/GameState';
import type { GameData, Researcher, LocationId, BreachedAnomaly, PropertyType, ActionCompletionToast } from '../game/types';
import { CELL_LOCATIONS } from '../utils/Constants';

export class BreachSystem {
  constructor(
    private gameState: GameStateManager,
    private gameData: GameData
  ) {}

  updateBreachTimers(): void {
    const breachedAnomalies = this.gameState.getBreachedAnomalies();
    
    for (const breached of breachedAnomalies) {
      // Don't decrement timer if containment is in progress
      if (breached.isContaining) continue;
      
      if (breached.escapeTimer > 0) {
        breached.escapeTimer--;
        
        if (breached.escapeTimer === 0) {
          this.handleEscape(breached);
        }
      }
    }
  }

  handleEscape(breached: BreachedAnomaly): void {
    const costs = this.gameData.costs;
    
    // Charge 300 budget for external containment
    this.gameState.spendBudget(costs.breach_containment_cost);
    
    // Remove breached anomaly (external teams handled it)
    this.gameState.removeBreachedAnomaly(breached);
    
    // Queue escape event for modal
    this.gameState.queueBreachEscape({
      anomalyName: breached.originalAnomaly.data.name,
      cost: costs.breach_containment_cost
    });
  }

  canContain(researcher: Researcher, cellId: LocationId): boolean {
    // Must be at the cell location
    if (researcher.location !== cellId) return false;

    // Must not be currently doing another action
    if (researcher.currentAction !== null && researcher.currentAction !== 'Contain') return false;

    // Cell must have a breached anomaly
    const breached = this.gameState.getBreachedInCell(cellId);
    if (!breached) return false;

    // Breached anomaly must not already be containing
    if (breached.isContaining) return false;

    return true;
  }

  startContainment(researcher: Researcher, cellId: LocationId): boolean {
    if (!this.canContain(researcher, cellId)) return false;

    const breached = this.gameState.getBreachedInCell(cellId);
    if (!breached) return false;

    // Calculate containment time based on Antiepistemic
    const antiepistemicCount = this.countProperty(breached.originalAnomaly.knownProperties, 'Antiepistemic');
    const baseTime = 30; // Base containment time
    const multiplier = 1 + antiepistemicCount; // 1x, 2x, or 3x
    let containmentTime = baseTime * multiplier;

    // Apply Veteran ability (reduce containment time by 33%)
    if (researcher.data.special_rules.includes('Veteran')) {
      containmentTime = Math.round(containmentTime * 0.67);
    }

    breached.isContaining = true;
    breached.containmentResearcherId = researcher.id;

    researcher.currentAction = 'Contain';
    researcher.actionTimer = containmentTime;
    researcher.actionTotalTime = containmentTime;

    return true;
  }

  completeContainment(researcher: Researcher, cellId: LocationId): void {
    const breached = this.gameState.getBreachedInCell(cellId);
    if (!breached) return;

    const anomaly = breached.originalAnomaly;
    const revealedProperties = anomaly.knownProperties;

    // Initialize containment result
    const containmentResult = {
      researcherId: researcher.id,
      anomalyName: anomaly.data.name,
      effects: {
        lethalBlocked: false,
        lethalKilled: false,
        memeticSanityLoss: 0,
        hyperrealAnchoringReduction: 0,
        immediateReEscape: false,
        cannotContain: false
      }
    };

    // Apply containment effects based on revealed properties

    // 1. Lethal effect
    const lethalCount = this.countProperty(revealedProperties, 'Lethal');
    if (lethalCount > 0) {
      const isProtected = this.checkSilverBulletProtection(researcher, anomaly);
      containmentResult.effects.lethalBlocked = isProtected;
      
      if (!isProtected) {
        containmentResult.effects.lethalKilled = true;
        this.gameState.removeResearcher(researcher.id);
      }
    }

    // 2. Memetic effect
    const memeticCount = this.countProperty(revealedProperties, 'Memetic');
    if (memeticCount > 0) {
      const sanityLoss = memeticCount * 20;
      containmentResult.effects.memeticSanityLoss = sanityLoss;
      
      const aliveResearcher = this.gameState.getResearcher(researcher.id);
      if (aliveResearcher) {
        aliveResearcher.currentSanity = Math.max(0, aliveResearcher.currentSanity - sanityLoss);
      }
    }

    // 3. Hyperreal effect
    const hyperrealCount = this.countProperty(revealedProperties, 'Hyperreal');
    if (hyperrealCount > 0) {
      containmentResult.effects.hyperrealAnchoringReduction = hyperrealCount;
      this.gameState.modifyAnchoring(-hyperrealCount);
    }

    // 4. Breaching re-escape check
    const breachingCount = this.countProperty(revealedProperties, 'Breaching');
    if (breachingCount >= 3) {
      // 3rd Breaching - 100% chance immediate re-escape (eternal cat and mouse)
      containmentResult.effects.immediateReEscape = true;
      // Re-escape to random cell, excluding current cell to ensure it moves to a different cell
      const currentBreachCell = breached.breachCellId;
      const emptyCells = CELL_LOCATIONS.filter(cid => {
        if (cid === currentBreachCell) return false; // Exclude current cell (where breach currently is)
        const hasAnomaly = this.gameState.getAnomalyInCell(cid) !== undefined;
        // Check for breached anomalies, but exclude the current one being contained
        const breachedInCell = this.gameState.getBreachedInCell(cid);
        const hasBreached = breachedInCell !== undefined && breachedInCell !== breached;
        return !hasAnomaly && !hasBreached;
      });

      let newBreachCellId: LocationId;
      if (emptyCells.length > 0) {
        // Filter out current cell as an extra safety measure (should already be excluded, but defensive)
        const validCells = emptyCells.filter(cid => cid !== currentBreachCell);
        if (validCells.length > 0) {
          newBreachCellId = validCells[Math.floor(Math.random() * validCells.length)];
        } else {
          // This should never happen if filtering is correct, but fallback to current cell
          newBreachCellId = currentBreachCell;
        }
      } else {
        // All other cells occupied, use current cell as fallback
        newBreachCellId = currentBreachCell;
      }

      // Reset breach - keep originCellId the same, update target
      breached.breachCellId = newBreachCellId;
      breached.escapeTimer = this.gameData.gameConfig.breaching_escape_timer;
      breached.isContaining = false;
      breached.containmentResearcherId = null;
    } else if (breachingCount >= 2) {
      // 2nd Breaching - 66% chance immediate re-escape
      if (Math.random() < 0.66) {
        containmentResult.effects.immediateReEscape = true;
        // Re-escape to random cell, excluding current cell to ensure it moves to a different cell
        const currentBreachCell = breached.breachCellId;
        const emptyCells = CELL_LOCATIONS.filter(cid => {
          if (cid === currentBreachCell) return false; // Exclude current cell (where breach currently is)
          const hasAnomaly = this.gameState.getAnomalyInCell(cid) !== undefined;
          // Check for breached anomalies, but exclude the current one being contained
          const breachedInCell = this.gameState.getBreachedInCell(cid);
          const hasBreached = breachedInCell !== undefined && breachedInCell !== breached;
          return !hasAnomaly && !hasBreached;
        });

        let newBreachCellId: LocationId;
        if (emptyCells.length > 0) {
          // Filter out current cell as an extra safety measure (should already be excluded, but defensive)
          const validCells = emptyCells.filter(cid => cid !== currentBreachCell);
          if (validCells.length > 0) {
            newBreachCellId = validCells[Math.floor(Math.random() * validCells.length)];
          } else {
            // This should never happen if filtering is correct, but fallback to current cell
            newBreachCellId = currentBreachCell;
          }
        } else {
          // All other cells occupied, use current cell as fallback
          newBreachCellId = currentBreachCell;
        }

        // Reset breach - keep originCellId the same, update target
        breached.breachCellId = newBreachCellId;
        breached.escapeTimer = this.gameData.gameConfig.breaching_escape_timer;
        breached.isContaining = false;
        breached.containmentResearcherId = null;
      }
    }

    // If containment succeeded, handle anomaly removal or return to cell
    if (!containmentResult.effects.immediateReEscape) {
      if (breached.shouldRemoveAfterContainment) {
        // Final research breach - remove completely (anomaly is dealt with)
        this.gameState.removeBreachedAnomaly(breached);
        // Also remove from anomalies if it still exists there
        const normalAnomaly = this.gameState.getAnomalyInCell(anomaly.cellId || breached.originCellId);
        if (normalAnomaly && normalAnomaly.data.code === anomaly.data.code) {
          this.gameState.removeAnomaly(normalAnomaly);
        }
      } else {
        // Not final research - return to original cell
        const originalCell = anomaly.cellId || breached.originCellId;
        const hasAnomaly = this.gameState.getAnomalyInCell(originalCell) !== undefined;
        if (!hasAnomaly) {
          anomaly.cellId = originalCell;
          this.gameState.addAnomaly(anomaly);
        }
        this.gameState.removeBreachedAnomaly(breached);
      }
    }

    // Queue containment result for modal
    this.gameState.queueContainmentCompletion(containmentResult);

    // Generate toast
    const researcherName = this.gameState.getResearcher(researcher.id)?.data.name || researcher.data.name;
    let summary = '';
    if (containmentResult.effects.immediateReEscape) {
      summary = `${containmentResult.anomalyName} - Contained but immediately re-escaped`;
    } else {
      summary = `${containmentResult.anomalyName} - Contained successfully`;
    }
    
    if (containmentResult.effects.lethalKilled) {
      summary += `, Researcher killed`;
    } else if (containmentResult.effects.memeticSanityLoss > 0) {
      summary += `, -${containmentResult.effects.memeticSanityLoss} Sanity`;
    }
    
    const toast: ActionCompletionToast = {
      researcherName: researcherName,
      location: cellId,
      actionType: 'Contain',
      summary: summary,
      timestamp: Date.now()
    };
    this.gameState.queueToast(toast);

    // Clear action (if researcher still alive)
    const aliveResearcher = this.gameState.getResearcher(researcher.id);
    if (aliveResearcher) {
      aliveResearcher.currentAction = null;
      aliveResearcher.actionTimer = 0;
      aliveResearcher.actionTotalTime = 0;
    }

    // Reset breach containment state
    breached.isContaining = false;
    breached.containmentResearcherId = null;
  }

  private countProperty(properties: PropertyType[], prop: PropertyType): number {
    return properties.filter(p => p === prop).length;
  }

  private checkSilverBulletProtection(researcher: Researcher, anomaly: Anomaly | BreachedAnomaly): boolean {
    if (!researcher.silverBullet) return false;

    const silverBullet = this.gameData.silverBullets.find(sb => sb.id === researcher.silverBullet);
    if (!silverBullet) return false;

    return anomaly.knownProperties.includes(silverBullet.effective_property);
  }
}
